import { useNavigate } from "react-router-dom";

import { BidTypeCd, ProjectType } from "@/consts/Bid";

import { BidContent, BidItem, GetBidListRequest } from "@/types/bid";

import { CreateFavoriteBid, DeleteFavoriteBid } from "@/services/Bid";

import {
  convertDate,
  getDate,
  isNotEmpty,
  formatToPercentString,
} from "@/libs/utils";

import { showToast } from "@/utils/toastUtils";

import CommonImg from "@/components/Common/Img";
import BidLimitContent from "@/components/BidLimitContent";
import DDayBadge from "@/components/DDayBadge";

import FavoriteIconFalse from "@/assets/images/favorite-icon-false.png";
import FavoriteIconTrue from "@/assets/images/favorite-icon-true.png";
import PositiveIcon from "@/assets/images/positive-icon.png";
import NegativeIcon from "@/assets/images/negative-icon.png";

type Props = {
  content: BidContent;
  onSearch: (toastFinish: boolean, newFilter?: GetBidListRequest) => void;
  projectType: string;
};
export default function BidList({ content, onSearch, projectType }: Props) {
  const navigate = useNavigate();
  const isOriginal = projectType === ProjectType.Original;
  const isProcure = projectType === ProjectType.Procure;

  const getLimitCondition = (bid: BidItem) => {
    switch (bid.limit_condition) {
      case "제한없음":
        return (
          <>
            <BidLimitContent
              title="제조물품"
              children={
                <CommonImg
                  src={PositiveIcon}
                  alt="True"
                  className="size-[18px]"
                />
              }
            />
            <BidLimitContent
              title="공급물품"
              children={
                <CommonImg
                  src={PositiveIcon}
                  alt="True"
                  className="size-[18px]"
                />
              }
            />
          </>
        );
      case "공급제한":
        return (
          <>
            <BidLimitContent
              title="제조물품"
              children={
                <CommonImg
                  src={PositiveIcon}
                  alt="True"
                  className="size-[18px]"
                />
              }
            />
            <BidLimitContent
              title="공급물품"
              children={
                <div className="text-gray-B font-semibold">
                  {bid.condition_fraction}
                </div>
              }
            />
          </>
        );
      case "제조제한":
        return (
          <>
            <BidLimitContent
              title="제조물품"
              children={
                <div className="text-gray-B font-semibold">
                  {bid.condition_fraction}
                </div>
              }
            />
            <BidLimitContent
              title="공급물품"
              children={
                <CommonImg
                  src={PositiveIcon}
                  alt="True"
                  className="size-[18px]"
                />
              }
            />
          </>
        );
      default:
        return <></>;
    }
  };
  const getConditionCheck = (bid: BidItem) => {
    if (bid.condition_check) {
      return (
        <BidLimitContent
          title="물품조건"
          children={
            <CommonImg src={PositiveIcon} alt="True" className="size-[18px]" />
          }
        />
      );
    } else {
      return (
        <BidLimitContent
          title="물품조건"
          children={
            <div className="text-gray-B font-semibold">
              {bid.condition_fraction}
            </div>
          }
        />
      );
    }
  };

  const handleClickFavorite = async (flag: boolean, project_id: string) => {
    try {
      const callApi = flag ? CreateFavoriteBid : DeleteFavoriteBid;
      const result = await callApi(projectType, project_id);
      if (result.data.success) {
        showToast({
          message: flag
            ? "관심 공고를 추가했습니다."
            : "관심 공고를 삭제했습니다.",
        });
        onSearch(false);
      } else {
        alert(result.data.error.content);
      }
    } catch (e) {
    } finally {
    }
  };

  const getOwnProductRatio = (value: number) => {
    if (!isNotEmpty(value) || value < -1) return "공고서 참조";
    return formatToPercentString(value, 2);
  };

  return (
    <>
      {content.items.map((bid, bidIndex) => (
        <div
          key={bidIndex}
          className="border-[1px] border-gray-D rounded-[12px] bg-white p-[20px] flex flex-col gap-[20px]"
        >
          <div className="flex justify-between">
            <div>
              <div className="flex items-center gap-[12px]">
                {bid.starred ? (
                  <CommonImg
                    src={FavoriteIconTrue}
                    alt="Favorite"
                    className="size-[20px] cursor-pointer z-10"
                    onClick={() => {
                      handleClickFavorite(false, bid.project_id);
                    }}
                  />
                ) : (
                  <CommonImg
                    src={FavoriteIconFalse}
                    alt="Favorite"
                    className="size-[20px] cursor-pointer z-10"
                    onClick={() => {
                      handleClickFavorite(true, bid.project_id);
                    }}
                  />
                )}
                <div
                  className="text-lg font-bold cursor-pointer"
                  onClick={() => {
                    navigate(
                      `/bid/domestic/detail/${projectType}/${bid.project_id}`,
                      {
                        state: {
                          keywords: bid.keyword_count_pairs,
                        },
                      }
                    );
                  }}
                >
                  {bid.title}
                </div>
                <div className="flex gap-[8px]">
                  {bid.keyword_count_pairs.map((tag, tagIndex) => (
                    <div
                      key={tagIndex}
                      className="px-[12px] py-[4px] bg-dark-red rounded-[15px] text-white text-[10px] font-bold flex items-center"
                    >
                      {`${tag.keyword} ${tag.count}회`}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-[10px] text-gray-A text-xs font-semibold">
              <span>공고 번호</span>
              <span className="text-gray-B">{bid.notice_no}</span>
            </div>
          </div>
          <div className="max-h-[42px] text-ellipsis overflow-hidden flex text-black-66 font-medium text-[15px]">
            {bid.summary}
          </div>
          <div className="grid grid-cols-5 gap-y-10">
            <div className="flex flex-col gap-[10px] text-xs">
              <div className="font-semibold">금액</div>
              <div className="text-gray-B">
                {bid.budget_amount?.toLocaleString() ?? "공고서 참조"}
              </div>
            </div>
            {isOriginal ? (
              <div className="flex flex-col gap-[10px] text-xs">
                <div className="font-semibold">상태 / TPP·SMF Rate</div>
                <div className="text-gray-B flex items-center gap-2">
                  <span>{bid.status}</span>
                  {(bid.tpp_ratio != null || bid.smf_ratio != null) && (
                    <span className="text-blue-ThermoFisher">
                      {bid.tpp_ratio != null && `TPP ${formatToPercentString(bid.tpp_ratio, 1)}`}
                      {bid.tpp_ratio != null && bid.smf_ratio != null && " / "}
                      {bid.smf_ratio != null && `SMF ${formatToPercentString(bid.smf_ratio, 1)}`}
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-[10px] text-xs">
                <div className="font-semibold">자사 제품 비중</div>
                <div className="text-gray-B">
                  <span className="text-gray-B">
                    {getOwnProductRatio(bid.own_product_ratio)}
                  </span>
                </div>
              </div>
            )}
            <div className="flex flex-col gap-[10px] text-xs">
              <div className="font-semibold">구분</div>
              <div className="text-gray-B">{BidTypeCd[bid.category]}</div>
            </div>
            <div className="flex flex-col gap-[10px] text-xs">
              <div className="font-semibold">수요기관</div>
              <div className="text-gray-B">{bid.dminst_nm}</div>
            </div>
            <div className="row-span-2 border-l-[1px] border-black-E2 text-xs pl-[20px]">
              <div className="grid grid-cols-2 gap-y-10">
                <BidLimitContent
                  title="업종조건"
                  children={
                    <div>
                      {bid.license_condition ? (
                        <CommonImg
                          src={PositiveIcon}
                          alt="Positive"
                          className="size-[18px]"
                        />
                      ) : (
                        <div className="text-gray-B font-semibold">
                          {bid.license_fraction}
                        </div>
                      )}
                    </div>
                  }
                />
                {isOriginal ? getLimitCondition(bid) : getConditionCheck(bid)}
                {isOriginal && (
                  <BidLimitContent
                    title="지역제한"
                    children={
                      bid.region_eligible ? (
                        <CommonImg
                          src={PositiveIcon}
                          alt="Postiive"
                          className="size-[18px]"
                        />
                      ) : (
                        <CommonImg
                          src={NegativeIcon}
                          alt="Negative"
                          className="size-[18px]"
                        />
                      )
                    }
                  />
                )}
              </div>
            </div>
            {!isProcure && (
              <div className="flex flex-col gap-[10px] text-xs">
                <div className="font-semibold">기업제한</div>
                <div className="text-gray-B">{bid.enterprise_condition}</div>
              </div>
            )}
            <div className="flex flex-col gap-[10px] text-xs">
              <div className="font-semibold">게시일</div>
              <div className="text-gray-B">
                {convertDate(getDate(bid.uploaded_dt))}
              </div>
            </div>
            <div className="flex flex-col gap-[10px] text-xs">
              <div className="font-semibold">마감일</div>
              <div className="text-gray-B flex items-center">
                {bid.close_dt === null ? (
                  "공고서 참조"
                ) : (
                  <>
                    {convertDate(getDate(bid.close_dt))}
                    <DDayBadge targetDate={bid.close_dt} />
                  </>
                )}
              </div>
            </div>
            {isOriginal && (
              <div className="flex flex-col gap-[10px] text-xs">
                <div className="font-semibold">자사 제품 비중</div>
                <div className="text-gray-B">
                  <span className="text-gray-B">
                    {getOwnProductRatio(bid.own_product_ratio)}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      ))}
    </>
  );
}
